/**
 * EmailSearchPicker — Combobox de busca para selecionar um e-mail.
 *
 * Lista campanhas (rascunhos do stepper) E templates (biblioteca reutilizável)
 * num único popover com busca por nome ou assunto. Cada resultado mostra a
 * origem (Campanha vs Template) num badge.
 */

import { useMemo, useState } from 'react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { Mail, Search, ChevronsUpDown, FileText, Send, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export type EmailPickerSource = 'campaign' | 'template';

export interface PickedEmail {
  source: EmailPickerSource;
  id: number;
  name: string;
  subject: string | null;
  status?: string;
}

interface EmailSearchPickerProps {
  selected?: PickedEmail | null;
  onPick: (email: PickedEmail) => void;
  placeholder?: string;
}

export default function EmailSearchPicker({ selected, onPick, placeholder }: EmailSearchPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const { data: campaignsData, isLoading: loadingCamps } = trpc.emailCampaigns.list.useQuery({});
  const { data: templates = [], isLoading: loadingTpls } = trpc.emailTemplates.list.useQuery();

  const campaigns: Array<{ id: number; name: string; subject: string | null; status: string }> =
    (campaignsData as any)?.rows ?? (Array.isArray(campaignsData) ? (campaignsData as any) : []);

  const tpls: Array<{ id: number; name: string; subject: string | null }> = (templates as any) ?? [];

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();

    const matches = (text?: string | null) =>
      !q || (text ?? '').toLowerCase().includes(q);

    const cs: PickedEmail[] = campaigns
      .filter((c) => matches(c.name) || matches(c.subject))
      .map((c) => ({ source: 'campaign' as const, id: c.id, name: c.name, subject: c.subject, status: c.status }));

    const ts: PickedEmail[] = tpls
      .filter((t) => matches(t.name) || matches(t.subject))
      .map((t) => ({ source: 'template' as const, id: t.id, name: t.name, subject: t.subject }));

    return { campaigns: cs, templates: ts, total: cs.length + ts.length };
  }, [campaigns, tpls, query]);

  const isLoading = loadingCamps || loadingTpls;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between text-sm font-normal h-auto py-2"
        >
          {selected ? (
            <span className="flex items-center gap-2 min-w-0 flex-1">
              {selected.source === 'campaign' ? (
                <Send className="w-3.5 h-3.5 text-blue-600 shrink-0" />
              ) : (
                <FileText className="w-3.5 h-3.5 text-violet-600 shrink-0" />
              )}
              <span className="truncate flex-1 text-left">{selected.name}</span>
              <Badge
                variant="outline"
                className={cn(
                  'text-[10px] shrink-0',
                  selected.source === 'campaign'
                    ? 'border-blue-200 text-blue-700'
                    : 'border-violet-200 text-violet-700',
                )}
              >
                {selected.source === 'campaign' ? 'Campanha' : 'Template'}
              </Badge>
            </span>
          ) : (
            <span className="flex items-center gap-2 text-muted-foreground">
              <Search className="w-3.5 h-3.5" />
              {placeholder ?? 'Buscar um e-mail por nome ou assunto…'}
            </span>
          )}
          <ChevronsUpDown className="w-3.5 h-3.5 text-muted-foreground shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-[--radix-popover-trigger-width] min-w-[360px]"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar por nome ou assunto…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {isLoading ? (
              <div className="p-4 text-sm text-muted-foreground text-center">Carregando…</div>
            ) : items.total === 0 ? (
              <CommandEmpty className="py-6">
                <div className="text-sm text-muted-foreground text-center">
                  <Mail className="w-5 h-5 mx-auto mb-2 opacity-40" />
                  {query ? 'Nada encontrado.' : 'Nenhum e-mail cadastrado ainda.'}
                </div>
              </CommandEmpty>
            ) : (
              <>
                {items.campaigns.length > 0 && (
                  <CommandGroup heading={`Campanhas (${items.campaigns.length})`}>
                    {items.campaigns.map((c) => (
                      <PickerItem
                        key={`c-${c.id}`}
                        email={c}
                        selected={selected?.source === 'campaign' && selected.id === c.id}
                        onPick={(e) => {
                          onPick(e);
                          setOpen(false);
                          setQuery('');
                        }}
                      />
                    ))}
                  </CommandGroup>
                )}
                {items.templates.length > 0 && (
                  <CommandGroup heading={`Templates (${items.templates.length})`}>
                    {items.templates.map((t) => (
                      <PickerItem
                        key={`t-${t.id}`}
                        email={t}
                        selected={selected?.source === 'template' && selected.id === t.id}
                        onPick={(e) => {
                          onPick(e);
                          setOpen(false);
                          setQuery('');
                        }}
                      />
                    ))}
                  </CommandGroup>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function PickerItem({
  email,
  selected,
  onPick,
}: {
  email: PickedEmail;
  selected: boolean;
  onPick: (e: PickedEmail) => void;
}) {
  const isCampaign = email.source === 'campaign';
  const Icon = isCampaign ? Send : FileText;
  const accent = isCampaign ? 'text-blue-600' : 'text-violet-600';
  const value = `${email.source}-${email.id}-${email.name}-${email.subject ?? ''}`;

  return (
    <CommandItem
      value={value}
      onSelect={() => onPick(email)}
      className="cursor-pointer aria-selected:bg-accent gap-2 py-2"
    >
      <Icon className={cn('w-3.5 h-3.5 shrink-0', accent)} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium truncate">{email.name}</span>
          {email.status && (
            <Badge variant="outline" className="text-[9px] py-0 h-4 shrink-0">{email.status}</Badge>
          )}
        </div>
        {email.subject && (
          <p className="text-[11px] text-muted-foreground truncate">{email.subject}</p>
        )}
      </div>
      {selected && <Check className="w-4 h-4 text-emerald-600 shrink-0" />}
    </CommandItem>
  );
}
